import React, { useState, useEffect, useCallback, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Tooltip,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axios from "axios";
import Swal from "sweetalert2";
import SignatureField from "../../../components/SignatureField";

const headerFieldSx = {
  "& .MuiInputBase-root": {
    height: 40,
    fontSize: "0.900rem",
    padding: "0 10px",
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  "& .MuiSvgIcon-root": { display: "none" },
  // Dark mode overrides for inputs
  ".dark-theme & .MuiInputBase-input": {
    color: "#fff !important",
  },
  ".dark-theme & .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7) !important",
  },
  ".dark-theme & .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.3) !important",
  },
};

// helper outside component so it doesn't trigger re-renders
const firstNonEmpty = (...vals) =>
  vals.find(
    (v) => v !== undefined && v !== null && String(v).trim() !== ""
  ) || "";

const CollateralNotePreview = ({ data, onClose }) => {
  const [note, setNote] = useState({});
  const [formMeta, setFormMeta] = useState(null); // full form record from GetPatientFormsbyId
  const [signatures, setSignatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [patientSigLink, setPatientSigLink] = useState("");
  const [openClientSignatureModal, setOpenClientSignatureModal] =
    useState(false);

  // Removed showSuccessAlert state as we are using Swal now

  const sigCanvasRef = useRef(null);

  const API_BASE_URL =
    process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

  // Derive a single form id from data (supports id, patientFormId, Id)
  const rawFormId =
    (data && (data.id ?? data.patientFormId ?? data.Id)) || "";
  const patientFormId = rawFormId;

  const { formTitle, practiceLogo, particeName } = data || {};

  const openClientModal = () => setOpenClientSignatureModal(true);
  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;
    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    setPatientSigLink(dataUrl);
    setSignatures((prev) => ({ ...prev, client: dataUrl }));
    closeClientModal();
  };

  const handleDeleteClientSignature = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) swalEl.style.zIndex = "9999";
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setPatientSigLink("");
      setSignatures((prev) => ({ ...prev, client: "" }));

      if (!patientFormId || !formMeta) return;

      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");
        const client = axios.create({
          baseURL: API_BASE_URL,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        // send full existing form meta, just clear signature fields
        const payload = {
          ...formMeta,
          Id: formMeta.Id ?? formMeta.id ?? patientFormId,
          patientFormId: patientFormId,
          clientsBase64: "", // Explicitly clear
          clientsURL: "",    // Explicitly clear
        };

        await client.post("/Forms/AddPatientForm", payload);

        Swal.fire({
          title: "Deleted!",
          text: "Signature has been removed.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) swalEl.style.zIndex = "9999";
          },
        });
      } catch (e) {
        console.error(e);
        Swal.fire({
          title: "Error!",
          text: "Failed to remove signature.",
          icon: "error",
          didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) swalEl.style.zIndex = "9999";
          },
        });
      }
    });
  };

  const handleSaveForm = async () => {
    if (!patientFormId || !formMeta) return;

    try {
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      const client = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      // Check if signature is Base64
      const isBase64 =
        patientSigLink && patientSigLink.toString().includes("base64");

      // Send the whole existing form record + new signature
      const payload = {
        ...formMeta,
        Id: formMeta.Id ?? formMeta.id ?? patientFormId,
        patientFormId: patientFormId,
        // If isBase64 is true, save to clientsBase64, otherwise empty
        clientsBase64: isBase64 ? patientSigLink : "",
        // If isBase64 is false (it's a URL), save to clientsURL, otherwise empty
        clientsURL: "",
      };

      await client.post("/Forms/AddPatientForm", payload);

      // --- Updated SweetAlert Success Popup with zIndex fix ---
      Swal.fire({
        title: "Form Saved",
        text: "Form saved successfully",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        didOpen: () => {
          // Ensure popup is above modal
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) swalEl.style.zIndex = "9999";
        },
      }).then(() => {
        // Logic to close the preview form after clicking OK
        if (onClose) {
          onClose();
        } else {
          try {
            const backdrop = document.querySelector(".MuiBackdrop-root");
            if (backdrop) {
              backdrop.click();
            } else {
              const closeButton = document.querySelector('[aria-label="close"]');
              if (closeButton) {
                closeButton.click();
              } else {
                window.history.back();
              }
            }
          } catch (err) {
            console.error("Could not close form:", err);
            window.location.reload();
          }
        }
      });

    } catch (e) {
      console.error(e);
      Swal.fire({
        title: "Error!",
        text: "Failed to save form.",
        icon: "error",
        didOpen: () => {
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) swalEl.style.zIndex = "9999";
        },
      });
    }
  };

  // Removed handleClose as it's merged into handleSaveForm

  const fmtDate = useCallback((val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }, []);

  useEffect(() => {
    let active = true;
    const revokeList = [];

    if (!patientFormId) {
      setLoading(false);
      setError("Form ID not provided. Cannot load collateral note.");
      return () => {};
    }

    const getPreviewLink = async (path, token) => {
      try {
        if (!path) return "";
        if (/^(https?:|data:)/i.test(path)) return path;

        const url = `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
          path
        )}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return "";

        const ct = (res.headers.get("content-type") || "").toLowerCase();
        if (ct.includes("text/plain")) {
          const link = await res.text();
          return link || "";
        }

        const blob = await res.blob();
        if (blob && blob.size > 0) {
          const objUrl = URL.createObjectURL(blob);
          revokeList.push(objUrl);
          return objUrl;
        }
        return "";
      } catch {
        return "";
      }
    };

    const fetchSignatures = async (noteData, token) => {
      const clientPath = firstNonEmpty(
        noteData.clientsURL,
        noteData.clientSignatureURL,
        noteData.patientSignatureURL,
        noteData.imageURLClientSignature
      );
      const loaded = {};
      if (clientPath) {
        const link = await getPreviewLink(clientPath, token);
        loaded.client = link;
        if (active) setPatientSigLink(link);
      }
      if (active) setSignatures(loaded);
    };

    const fetchNote = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");
        if (!token) throw new Error("Authentication token not found.");
        if (!API_BASE_URL) throw new Error("API base URL is not configured.");

        const [formRes, noteRes] = await Promise.all([
          fetch(
            `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
          fetch(
            `${API_BASE_URL}/CollateralNote/getCollateralNoteByFormId?patientFormId=${patientFormId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        if (formRes.status === 204 || noteRes.status === 204) {
          if (active) {
            setNote({});
            setFormMeta(null);
            setSignatures({});
          }
          return;
        }

        if (!formRes.ok || !noteRes.ok)
          throw new Error("Failed to load data.");

        const formJson = await formRes.json();
        const noteJson = await noteRes.json();

        // formJson might be array or object
        const formData = Array.isArray(formJson)
          ? formJson[0] || {}
          : formJson || {};
        const noteData = noteJson || {};

        if (active) {
          setFormMeta(formData);
          setNote({ ...noteData, ...formData });
          await fetchSignatures({ ...noteData, ...formData }, token);
        }
      } catch (err) {
        console.error(err);
        if (active) setError(err.message || "Failed to load form.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchNote();

    return () => {
      active = false;
      try {
        revokeList.forEach(
          (u) => u.startsWith("blob:") && URL.revokeObjectURL(u)
        );
      } catch {}
    };
  }, [patientFormId, API_BASE_URL]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "60vh",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  const sigDate = (v) => {
    const d = fmtDate(v);
    return d === "—" ? "" : d;
  };

  const clientSignedDate =
    note.patientSignatureDATE || note.clientSignatureDate;

  const rehabMsg = note.messagerehabWorker || "";
  const practitionerMsg = note.messagepractitioner || "";
  const staffMsg = note.messageinternSign || "";
  const supervisorMsg = note.messageSupervisorSign || "";
  const directorMsg = note.messagetreatmentdirector || "";

  const renderClientSignatureRow = () => {
    const dateText = sigDate(clientSignedDate);

    return (
      <tr>
        <td style={{ padding: 8, width: "25%", verticalAlign: "top" }}>
          <Typography>Client Signature:</Typography>
        </td>
        <td style={{ padding: 8, width: "75%", verticalAlign: "top" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {patientSigLink ? (
              <>
                <Box
                  component="img"
                  src={patientSigLink}
                  alt="Client Signature"
                  sx={{
                    width: 200,
                    height: 80,
                    border: "2px solid #000",
                    backgroundColor: "#f5f5f5",
                    // Signature dark mode handling
                    ".dark-theme &": {
                        backgroundColor: "#ffffff", 
                        border: "2px solid #ccc"
                    },
                    p: 1,
                    borderRadius: 1,
                    objectFit: "contain",
                  }}
                />
                {dateText && (
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                    Signed on {dateText}
                  </Typography>
                )}
                <Tooltip title="Edit Signature" arrow>
                  <Button
                    onClick={openClientModal}
                    variant="outlined"
                    size="small"
                    style={{ minWidth: "32px", padding: "4px" }}
                    aria-label="Edit Signature"
                  >
                    <EditIcon />
                  </Button>
                </Tooltip>

                <Tooltip title="Remove Signature" arrow>
                  <Button
                    onClick={handleDeleteClientSignature}
                    style={{ minWidth: "32px", padding: "4px" }}
                    aria-label="Remove Signature"
                  >
                    <RemoveCircleOutlineIcon style={{ color: "red" }} />
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Link component="button" onClick={openClientModal}>
                Open signing window
              </Link>
            )}
          </Box>
        </td>
      </tr>
    );
  };

  const renderMessageRow = (label, message) => (
    <tr>
      <td style={{ padding: 8, width: "25%", verticalAlign: "top" }}>
        <Typography>{label}</Typography>
      </td>
      <td style={{ padding: 8, width: "50%", verticalAlign: "top" }}>
        {message ? (
          <Typography sx={{ fontWeight: 600 }}>{message}</Typography>
        ) : (
          <Typography color="text.secondary">—</Typography>
        )}
      </td>
      <td style={{ padding: 8, width: "25%", verticalAlign: "top" }} />
    </tr>
  );

  return (
    <>
      <Paper
        sx={{
          p: 0,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
          // Dark Mode for Main Container
          ".dark-theme &": {
            bgcolor: "#0e1726",
            color: "#fff",
            boxShadow: "none"
          }
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (theme) => theme.zIndex.appBar,
            bgcolor: "#fff",
            p: 4,
            pb: 0,
            mb: 3,
            // Dark Mode for Sticky Header
            ".dark-theme &": {
                bgcolor: "#0e1726",
                color: "#fff",
                borderBottom: "1px solid #1f2937"
            }
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ position: "absolute", top: 16, left: 16 }}
          >
            <strong>DOS:</strong> {fmtDate(note.assessmentDate)}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              position: "absolute",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              fontWeight: "bold",
            }}
          >
            {formTitle || note.customFormTitle || "Collateral Note"}
          </Typography>

          {practiceLogo && (
            <Box
              component="img"
              src={practiceLogo}
              alt="Practice Logo"
              sx={{ width: 65, height: 65, borderRadius: 1, mb: 2, mx: "auto" }}
            />
          )}
          <Typography
            variant="h6"
            sx={{ textAlign: "center", fontWeight: "bold" }}
          >
            {note.particeName || particeName || ""}
          </Typography>
          <Box sx={{ height: 28 }} />
          <Divider />
        </Box>

        <Box sx={{ px: 4 }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                <strong>File #</strong> {note.fileno || note.accountNo || "—"}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: "center", mt: { xs: 1, md: 0 } }}
            >
              <Typography variant="body2">
                <strong>Client:</strong>{" "}
                {note.patientName || note.PatientNameclient || "—"}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: "left", md: "right" },
                mt: { xs: 1, md: 0 },
              }}
            >
              <Typography variant="body2">
                <strong>DOB:</strong> {fmtDate(note.dob)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />
        </Box>

        <Box sx={{ px: 4, mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: 4,
              mb: 1,
            }}
          >
            <TextField
              label="Title"
              value={note.customFormTitle || formTitle || "—"}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              sx={headerFieldSx}
            />

            <TextField
              label="Session Date"
              value={fmtDate(note.sessionDate || note.assessmentDate)}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              sx={headerFieldSx}
            />
          </Box>
        </Box>

        <Box sx={{ px: 4, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={note.isPrinted || false} disabled />}
                label="Printed and Filed"
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={note.Isinactive || false} disabled />}
                label="Inactive"
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={note.isAuditReady || false} disabled />}
                label="Audit Ready"
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={note.islocked || false} disabled />}
                label="Lock"
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ px: 4, mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: "bold" }}>
            Collateral Note
          </Typography>
        </Box>

        <Box sx={{ px: 4, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
                <Typography sx={{ fontWeight: 600, minWidth: 100 }}>
                  Client Name:
                </Typography>
                <Typography>
                  {note.patientName || note.PatientNameclient || "—"}
                </Typography>
              </Box>

              <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
                <Typography sx={{ fontWeight: 600, minWidth: 100 }}>
                  File No:
                </Typography>
                <Typography>{note.accountNo || note.fileno || "—"}</Typography>
              </Box>

              <Box sx={{ mb: 1, display: "flex", gap: 2, alignItems: "center" }}>
                <Typography sx={{ fontWeight: 600, minWidth: 100 }}>
                  Contact Type:
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox checked={note.collateralType || false} disabled />
                  }
                  label="Collateral"
                />
                <FormControlLabel
                  control={<Checkbox checked={note.otherType || false} disabled />}
                  label="Others"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="DOB"
                  value={fmtDate(note.dob)}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={headerFieldSx}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Length of Session (mins)"
                  value={note.lengthofSession || "—"}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={headerFieldSx}
                />
              </Box>

              <Box sx={{ mb: 1 }}>
                <TextField
                  label="Other Detail"
                  value={note.otherdetail || "—"}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={headerFieldSx}
                  multiline
                  minRows={1}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ px: 4, mb: 2 }}>
          <TextField
            fullWidth
            label="Description"
            value={note.description || "—"}
            InputProps={{ readOnly: true }}
            multiline
            minRows={3}
            sx={{
               ".dark-theme & .MuiInputBase-root": { color: "#fff !important" },
               ".dark-theme & .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3) !important" },
               ".dark-theme & .MuiInputLabel-root": { color: "rgba(255,255,255,0.7) !important" }
            }}
          />
        </Box>

        <Divider sx={{ my: 3, mx: 4 }} />

        <Box sx={{ px: 4, pb: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Signatures
          </Typography>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {renderClientSignatureRow()}
              {renderMessageRow("Mental Health Rehab Worker:", rehabMsg)}
              {renderMessageRow("Mental Health Practitioner:", practitionerMsg)}
              {renderMessageRow("Staff Signature (required):", staffMsg)}
              {renderMessageRow("Supervisor Signature:", supervisorMsg)}
              {renderMessageRow("ARMHS Treatment Director:", directorMsg)}
            </tbody>
          </table>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
            >
              Save Form
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
        sx={{
            ".dark-theme & .MuiPaper-root": {
                backgroundColor: "#0e1726",
                color: "#fff"
            }
        }}
      >
        <DialogTitle>Add Client Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1, backgroundColor: "#fff" }}>
            <SignatureField
              ref={sigCanvasRef}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (sigCanvasRef.current) {
                sigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CollateralNotePreview;